import React, { FC, ReactNode } from "react";
import classNames from "classnames";
import { MENU_ICONS, OverflowMenu } from "../../../../../components/MenuOptions/OverflowMenu";
import type { OverflowMenuGroup } from "../../../../../components/MenuOptions/OverflowMenu";
import { intervalLabel } from "../../../../../utils/plans";
import { IconGoToTabGlyph, IconRefreshGlyph } from "../../../videos/selectedVideoCardV2/panels";
import { productUrlHref } from "../pageModel";
import { btnClass, glyph } from "../components/ui";
import { useMenuTrigger } from "../components/useMenuTrigger";
import { urlParts } from "./pageModel";
import type { PdpUrl } from "./pageModel";

/**
 * The page block's identity row and its actions (PageBlock.tsx lays them
 * out): the URL, the owner, the cadence or paused chip, the score button and
 * the page ⋮.
 */

/** A chip in the identity row: 22px, a full pill, never wrapping. */
export const CHIP = "inline-flex items-center h-[22px] rounded-full whitespace-nowrap shrink-0";

// The page ⋮, sized like the buttons beside it: 36px, 44px below md. The
// trigger is OverflowMenu's fixed w-7 h-[30px] (OverflowMenu.tsx:103); this
// wrapper outranks it, as ProductHeaderMenu's does. Items are OverflowMenu's
// own 38px (48px below 600px) — the menu is portalled, out of reach.
const MENU_TRIGGER = "inline-flex shrink-0 [&>button]:h-9 [&>button]:w-9 max-md:[&>button]:h-11 max-md:[&>button]:w-11";

/** The page URL: host in --text, path in --text-2, ellipsis, external glyph (README:164). */
export const PageUrlLink: FC<{ url: string }> = ({ url }) => {
  const { host, path } = urlParts(url);
  // Free text from a form: only http(s) becomes a link (productUrlHref, the header's link rule).
  const href = productUrlHref(url);
  const text = (
    <span className="truncate min-w-0">
      {host || url}
      <span className="text-text-2">{path}</span>
    </span>
  );
  if (!href) {
    return (
      <span className="flex items-center min-w-0 max-w-full text-text-1" style={{ fontSize: 13.5 }} title={url}>
        {text}
      </span>
    );
  }
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      title={url}
      className="flex items-center gap-1.5 self-start min-w-0 max-w-full text-text-1 hover:text-accent-text transition-colors"
      style={{ fontSize: 13.5 }}
    >
      {text}
      <span className="inline-flex shrink-0 text-text-2" aria-hidden="true">
        <IconGoToTabGlyph size={12} strokeWidth={2} />
      </span>
      <span className="sr-only"> (opens in a new tab)</span>
    </a>
  );
};

/** The owner brand, 12.5px --text-2. */
export const OwnerLabel: FC<{ row: PdpUrl }> = ({ row }) =>
  row.brand?.name ? (
    <span className="text-text-2" style={{ fontSize: 12.5 }}>
      {row.brand.name}
    </span>
  ) : null;

/**
 * The cadence chip, "Weekly" — with the modal's tooltip (:519-527), and the
 * same words for screen readers, since a title never reaches a keyboard.
 */
export const CadenceChip: FC<{ interval?: string | null }> = ({ interval }) =>
  interval ? (
    <span
      className={classNames(CHIP, "px-[9px] bg-surface-2 text-text-1")}
      style={{ fontSize: 11 }}
      title="PDP analysis interval for this URL (capped by the brand's plan)"
    >
      {intervalLabel(interval)}
      <span className="sr-only"> analysis interval, capped by the brand&apos;s plan</span>
    </span>
  ) : null;

/** "⏸ Scoring paused" in place of the cadence, with the modal's tooltip (:514-520). */
export const PausedChip: FC = () => (
  <span
    className={classNames(CHIP, "gap-[5px] px-[9px] bg-surface-2 text-text-1")}
    style={{ fontSize: 11 }}
    title="Not scored: the brand's plan is at its scoring limit or scoring was paused"
  >
    <span className="inline-flex" aria-hidden="true">
      {glyph("pause", 10, 2)}
    </span>
    Scoring paused
  </span>
);

/** Score now (staff, sync) / Run report (everyone else) — the soft "tint" start action. */
export const ScoreButton: FC<{ label: string; disabled: boolean; onClick: () => void }> = ({ label, disabled, onClick }) => (
  <button type="button" onClick={onClick} disabled={disabled} className={btnClass("tint", { disabled })}>
    <IconRefreshGlyph size={14} strokeWidth={2} />
    {label}
  </button>
);

interface PageMenuProps {
  paused: boolean;
  toggling: boolean;
  onToggleScoring: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

/** A menu item's glyph, out of the accessibility tree: the item's label names it. */
const menuGlyph = (icon: ReactNode) => (
  <span className="inline-flex" aria-hidden="true">
    {icon}
  </span>
);

/**
 * The page ⋮ (README:170): Pause / Resume scoring, Edit URL, SKUs and
 * refresh, Delete page… — `OverflowMenu` unedited. It draws a label or a rule
 * before every group (OverflowMenu.tsx:127-131), so the first group carries a
 * "Page" label (an empty one would put a rule at the top), as the header's
 * "Edit" label does; the destructive item sits below a rule. `useMenuTrigger`
 * moves focus past that label into the items, for the keyboard.
 *
 * 232px wide: the longest label, "Edit URL, SKUs and refresh", needs 221px
 * of menu at 13px (icon, gaps and item padding included); 212 cut it to
 * "…and ref…".
 */
export const PageMenu: FC<PageMenuProps> = ({ paused, toggling, onToggleScoring, onEdit, onDelete }) => {
  const trigger = useMenuTrigger();
  const groups: OverflowMenuGroup[] = [
    {
      label: "Page",
      items: [
        {
          key: "scoring",
          label: paused ? "Resume scoring" : "Pause scoring",
          icon: menuGlyph(paused ? glyph("play") : glyph("pause")),
          onClick: onToggleScoring,
          disabled: toggling,
        },
        { key: "edit", label: "Edit URL, SKUs and refresh", icon: menuGlyph(MENU_ICONS.edit), onClick: onEdit },
      ],
    },
    {
      label: "",
      items: [{ key: "delete", label: "Delete page…", icon: menuGlyph(MENU_ICONS.trash), danger: true, onClick: onDelete }],
    },
  ];
  return (
    <div ref={trigger.ref} onClickCapture={trigger.onClickCapture} className={MENU_TRIGGER}>
      <OverflowMenu groups={groups} ariaLabel="Page actions" width={232} />
    </div>
  );
};
